import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SensitiveData } from "@/components/sensitive-data";
import { formatCurrency } from "@/lib/utils";
import type { ForecastPerformance } from "../types";

interface CostForecastCardProps {
  performance: ForecastPerformance;
  onViewForecast: () => void;
}

export function CostForecastCard({ performance, onViewForecast }: CostForecastCardProps) {
  const { summary, rows, costUnderBudget } = performance;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <CardTitle className="text-sm font-medium">Cost Forecast</CardTitle>
          {summary && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
              costUnderBudget
                ? 'bg-success-100 text-success-700'
                : 'bg-error-100 text-error-700'
            }`}>
              {costUnderBudget ? 'Under Budget' : 'Over Budget'}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">
          {summary
            ? <SensitiveData>{formatCurrency(summary.totalForecastCost)}</SensitiveData>
            : <span className="text-muted-foreground">—</span>
          }
        </div>
        <p className="text-xs text-muted-foreground mt-1">Total Forecast Cost (EAC)</p>
        {summary ? (
          <div className="mt-3 space-y-1.5 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Budget</span>
              <SensitiveData><span className="font-medium">{formatCurrency(rows.totalBudgetCost)}</span></SensitiveData>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Actuals to Date</span>
              <SensitiveData><span className="font-medium">{formatCurrency(rows.actualsToDate)}</span></SensitiveData>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Forecast to Complete</span>
              <SensitiveData><span className="font-medium">{formatCurrency(rows.forecastToComplete)}</span></SensitiveData>
            </div>
            <div className="border-t pt-1.5 mt-1" />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cost Variance</span>
              <SensitiveData>
                <span className={`font-medium ${costUnderBudget ? 'text-success-600' : 'text-error-600'}`}>
                  {costUnderBudget ? '+' : ''}{formatCurrency(rows.costVariance)} ({costUnderBudget ? '+' : ''}{rows.costVariancePercent.toFixed(1)}%)
                </span>
              </SensitiveData>
            </div>
          </div>
        ) : (
          <p className="mt-3 text-xs text-muted-foreground italic">No forecast data committed yet.</p>
        )}
        <div className="mt-3 pt-2 border-t">
          <button
            type="button"
            onClick={onViewForecast}
            className="text-xs text-primary underline underline-offset-2 hover:opacity-70 transition-opacity"
          >
            View Forecast →
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
